import { PropsWithChildren } from 'react';

import { cn } from '@components/primitives/utilities';

import { HeaderContext, HeaderSize } from './context';
import { HeaderActions } from './HeaderActions';
import { HeaderIcon } from './HeaderIcon';
import { HeaderSuffix } from './HeaderSuffix';
import { HeaderTitle } from './HeaderTitle';

import css from './styles.module.css';

export interface HeaderProps extends PropsWithChildren {
  /**
   * Aligns the title line and the actions against each other.
   * @default 'end'
   */
  align?: 'start' | 'end' | 'center';
  className?: string;
  /**
   * Draws a rule under the header, separating it from the content below.
   * @default true
   */
  hasBorder?: boolean;
  /**
   * The type scale. `Header.Title` also derives its heading level from this —
   * `lg` renders an `h1`, `md` an `h2` and `sm` an `h3`.
   * @default 'md'
   */
  size?: HeaderSize;
}

/**
 * Children render as direct children of the root, in the order they are
 * written. Anything that is not a sub-component is description content.
 */
export function Header({
  align = 'end',
  className,
  children,
  hasBorder = true,
  size = 'md',
}: HeaderProps) {
  return (
    <HeaderContext.Provider value={{ size }}>
      <header
        className={cn(css.header, !hasBorder && css.noBorder, className)}
        data-align={align}
        data-size={size}
      >
        {children}
      </header>
    </HeaderContext.Provider>
  );
}

Header.Icon = HeaderIcon;
Header.Title = HeaderTitle;
Header.Suffix = HeaderSuffix;
Header.Actions = HeaderActions;

export type { HeaderSize } from './context';
export { useHeaderContext } from './context';
export type { HeaderTitleProps } from './HeaderTitle';
